import * as THREE from 'three';
import type { Tickable } from '../engine/Game';
import { bus } from '../engine/EventBus';
import { save } from '../engine/SaveSystem';

interface Sparkle {
  id: string;
  group: THREE.Group;
  light: THREE.PointLight;
  basePos: THREE.Vector3;
  collected: boolean;
}

// Hand-picked hiding spots.
const LOCATIONS: Array<{ id: string; pos: [number, number, number] }> = [
  { id: 's1', pos: [40, 7.5, 10] },        // library rooftop
  { id: 's2', pos: [-10, 7, 35] },         // cafe rooftop
  { id: 's3', pos: [-40, 8, -5] },         // shop mushroom top
  { id: 's4', pos: [30, 1, -38] },         // behind pond
  { id: 's5', pos: [60, 3, 50] },          // map edge NE
  { id: 's6', pos: [-60, 3, -50] },        // map edge SW
  { id: 's7', pos: [70, 1.5, -30] },       // map edge E
  { id: 's8', pos: [-70, 1.5, 30] },       // map edge W
  { id: 's9', pos: [5, 1.5, -55] },        // behind player house
  { id: 's10', pos: [15, 2, 15] },         // open field
];

export class Secrets implements Tickable {
  readonly group = new THREE.Group();
  private sparkles: Sparkle[] = [];
  private particles: Array<{ mesh: THREE.Mesh; life: number; vel: THREE.Vector3 }> = [];

  constructor() {
    for (const l of LOCATIONS) {
      const g = new THREE.Group();
      const orb = new THREE.Mesh(
        new THREE.SphereGeometry(0.25, 16, 12),
        new THREE.MeshStandardMaterial({
          color: '#ffffff',
          emissive: '#ffe8a8',
          emissiveIntensity: 1.4,
        }),
      );
      g.add(orb);
      const halo = new THREE.Mesh(
        new THREE.SphereGeometry(0.45, 16, 12),
        new THREE.MeshBasicMaterial({
          color: '#fff6c0',
          transparent: true,
          opacity: 0.25,
          depthWrite: false,
        }),
      );
      g.add(halo);
      g.position.set(l.pos[0], l.pos[1], l.pos[2]);

      const light = new THREE.PointLight('#ffe8a8', 1.0, 5, 2);
      g.add(light);

      const already = save.data.collectedSecrets.includes(l.id);
      g.visible = !already;

      this.group.add(g);
      this.sparkles.push({
        id: l.id,
        group: g,
        light,
        basePos: new THREE.Vector3(...l.pos),
        collected: already,
      });
    }
  }

  get total(): number { return this.sparkles.length; }

  tick(dt: number, elapsed: number): void {
    for (const s of this.sparkles) {
      if (s.collected) continue;
      s.group.position.y = s.basePos.y + Math.sin(elapsed * 2 + s.basePos.x) * 0.2;
      s.group.rotation.y += dt * 1.5;
    }

    // Particle bursts.
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= dt;
      p.mesh.position.addScaledVector(p.vel, dt);
      p.vel.y -= 1.5 * dt;
      (p.mesh.material as THREE.MeshBasicMaterial).opacity = Math.max(0, p.life);
      if (p.life <= 0) {
        this.group.remove(p.mesh);
        this.particles.splice(i, 1);
      }
    }
  }

  checkCollect(playerPos: THREE.Vector3): void {
    for (const s of this.sparkles) {
      if (s.collected) continue;
      if (playerPos.distanceTo(s.group.position) < 1.8) {
        s.collected = true;
        s.group.visible = false;
        save.data.collectedSecrets.push(s.id);
        save.data.coins += 10;
        bus.emit('secret:collected', { id: s.id, total: save.data.collectedSecrets.length });
        bus.emit('coins:changed', { coins: save.data.coins });
        bus.emit('toast', { message: '✨ +10 coins — a secret revealed!' });
        bus.emit('save:dirty', {});
        this.burst(s.basePos);
      }
    }
  }

  private burst(pos: THREE.Vector3): void {
    const geo = new THREE.SphereGeometry(0.08, 6, 6);
    for (let i = 0; i < 14; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: '#ffe8a8',
        transparent: true,
        opacity: 1,
        depthWrite: false,
      });
      const m = new THREE.Mesh(geo, mat);
      m.position.copy(pos);
      const v = new THREE.Vector3(
        (Math.random() - 0.5) * 4,
        2 + Math.random() * 2,
        (Math.random() - 0.5) * 4,
      );
      this.group.add(m);
      this.particles.push({ mesh: m, life: 1.0, vel: v });
    }
  }
}
